import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Filter, Search as SearchIcon } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import Breadcrumbs from "../components/Breadcrumbs";
import CostPriceExplainer from "../components/CostPriceExplainer";
import PaymentsPausedNotice from "../components/PaymentsPausedNotice";
import { useCheckoutEnabled } from "../lib/checkout";
import { EmptyState, ErrorState, SkeletonGrid } from "../components/States";
import useSeo from "../hooks/useSeo";

const PER = 24;

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const payments = useCheckoutEnabled();
  useSeo({
    title: "Surplus 4WD Stock at Cost Price - NQ Springs & 4x4 Accessories",
    description:
      "Genuine surplus and superseded 4WD suspension, lift kits and accessories sold at our cost price. Shipped Australia-wide from Townsville. Once it is gone, it is gone.",
    canonical: "/shop",
  });
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState(params.get("search") || "");

  const search = params.get("search") || "";
  const category = params.get("category") || "";
  const sort = params.get("sort") || "name";
  const page = Number(params.get("page") || 1);

  const load = useCallback(() => {
    setData(null);
    setError(false);
    api
      .get("/products", {
        params: {
          search: search || undefined,
          category: category || undefined,
          sort,
          limit: PER,
          skip: (page - 1) * PER,
        },
      })
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [search, category, sort, page]);

  useEffect(() => {
    load();
  }, [load]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    if (k !== "page") next.delete("page");
    setParams(next);
  };

  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / PER));

  return (
    <div data-testid="shop-page">
      <section
        className="border-b"
        style={{ borderColor: "rgba(255,255,255,0.08)", background: "var(--nq-ink)" }}
      >
        <div className="container-nq py-10">
          <Breadcrumbs items={[{ slug: "shop", label: "Surplus Stock" }]} />
          <p className="eyebrow mt-4">Cost price clear-out</p>
          <h1 className="mt-2 text-3xl font-bold md:text-5xl">Surplus Stock</h1>
          <p className="mt-3 max-w-2xl text-sm text-[color:var(--nq-stone)] md:text-base">
            Genuine parts and accessories in stock now, sold at our cost price and ready to ship
            Australia-wide. No markups, no margin - and nothing here is restocked once it sells.
          </p>
        </div>
      </section>

      <div className="container-nq space-y-5 pt-8">
        {!payments && <PaymentsPausedNotice />}
        <CostPriceExplainer />
      </div>

      <div className="container-nq section-nq">
        <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-center">
          <form
            className="flex flex-1 items-center gap-2 rounded-lg border px-3 py-2"
            style={{ borderColor: "rgba(255,255,255,0.14)" }}
            onSubmit={(e) => {
              e.preventDefault();
              setParam("search", q.trim());
            }}
          >
            <SearchIcon className="h-4 w-4 gold-text" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search products..."
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-[color:var(--nq-stone)]/60"
              data-testid="shop-search-input"
            />
            <button type="submit" className="btn-outline-nq px-3 py-1 text-xs" data-testid="shop-search-submit">
              Search
            </button>
          </form>

          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-[color:var(--nq-stone)]" />
            <select
              value={category}
              onChange={(e) => setParam("category", e.target.value)}
              className="rounded-lg border bg-transparent px-3 py-2 text-sm outline-none"
              style={{ borderColor: "rgba(255,255,255,0.14)", background: "var(--nq-ink)" }}
              data-testid="shop-category-select"
            >
              <option value="">All categories</option>
              {(data?.categories || []).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            <select
              value={sort}
              onChange={(e) => setParam("sort", e.target.value)}
              className="rounded-lg border bg-transparent px-3 py-2 text-sm outline-none"
              style={{ borderColor: "rgba(255,255,255,0.14)", background: "var(--nq-ink)" }}
              data-testid="shop-sort-select"
            >
              <option value="name">Sort: Name</option>
              <option value="price-asc">Price: low to high</option>
              <option value="price-desc">Price: high to low</option>
            </select>
          </div>
        </div>

        {error ? (
          <ErrorState message="Could not load products." onRetry={load} />
        ) : !data ? (
          <SkeletonGrid count={8} />
        ) : data.products.length === 0 ? (
          <EmptyState
            title="No products found"
            message="Try a different search or clear your filters."
            action={
              <Link to="/shop" className="btn-outline-nq mt-2">
                Clear filters
              </Link>
            }
          />
        ) : (
          <>
            <p className="mb-5 text-xs text-[color:var(--nq-stone)]" data-testid="shop-result-count">
              Showing {(page - 1) * PER + 1}–{Math.min(page * PER, total)} of {total} products
            </p>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="shop-grid">
              {data.products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>

            {pages > 1 && (
              <div className="mt-10 flex justify-center gap-2">
                {Array.from({ length: pages }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setParam("page", String(i + 1))}
                    className={i + 1 === page ? "btn-gold px-4 py-2 text-xs" : "btn-outline-nq px-4 py-2 text-xs"}
                    data-testid="shop-page-button"
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
